import React, { useState } from 'react';
import {
  calculateSpringDroughtCode,
  getParameterSuggestions,
  CarryoverCategory,
  WettingCategory
} from '../../../easymap/mapcontrols/DCCalculator/DCCalculator';

interface DCCalculatorPanelProps {
  onUseValue: (dc: number) => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px',
    color: 'var(--em3-color-text-primary)',
  },
  titleBlock: {
    marginBottom: '16px',
  },
  title: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--em3-color-text-primary)',
  },
  subtitle: {
    margin: '2px 0 0',
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '12px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  label: {
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
    fontWeight: 500,
  },
  input: {
    backgroundColor: 'var(--em3-color-background-input)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-color-text-primary)',
    fontSize: '13px',
    padding: '8px 12px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  select: {
    backgroundColor: 'var(--em3-color-background-input)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-color-text-primary)',
    fontSize: '13px',
    padding: '8px 12px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    cursor: 'pointer',
  },
  paramDisplay: {
    backgroundColor: 'var(--em3-color-background-main)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '8px 10px',
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
    marginBottom: '12px',
  },
  button: {
    backgroundColor: 'var(--em3-button-secondary-bg)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-button-secondary-text)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    padding: '8px 14px',
    width: '100%',
    marginBottom: '10px',
  },
  buttonUse: {
    backgroundColor: 'var(--em3-button-primary-bg)',
    border: 'none',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-button-primary-text)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    padding: '8px 14px',
    width: '100%',
  },
  buttonDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  resultBox: {
    backgroundColor: 'var(--em3-color-background-main)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '10px',
    fontSize: '13px',
    color: 'var(--em3-color-text-primary)',
    marginBottom: '10px',
  },
  errorBox: {
    backgroundColor: 'var(--em3-color-danger-bg, rgba(200, 50, 50, 0.12))',
    border: '1px solid var(--em3-color-danger-border, rgba(200, 50, 50, 0.4))',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '8px 10px',
    fontSize: '12px',
    color: 'var(--em3-color-danger)',
    marginBottom: '10px',
  },
};

const DCCalculatorPanel: React.FC<DCCalculatorPanelProps> = ({ onUseValue }) => {
  const [fallDC, setFallDC] = useState<string>('');
  const [winterPrecip, setWinterPrecip] = useState<string>('');
  const [carryoverCategory, setCarryoverCategory] = useState<CarryoverCategory>('moderate');
  const [wettingCategory, setWettingCategory] = useState<WettingCategory>('moderate');
  const [result, setResult] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params = getParameterSuggestions(carryoverCategory, wettingCategory);

  const handleCalculate = () => {
    const fallDCNum = parseFloat(fallDC);
    const winterPrecipNum = parseFloat(winterPrecip);

    try {
      const springDC = calculateSpringDroughtCode(
        fallDCNum,
        winterPrecipNum,
        params.carryoverFraction,
        params.wettingEfficiency
      );
      setResult(springDC);
      setError(null);
    } catch (e: unknown) {
      setResult(null);
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Calculation failed');
      }
    }
  };

  const isCalculateDisabled = fallDC.trim() === '' || winterPrecip.trim() === '';

  return (
    <div style={styles.container}>
      <div style={styles.titleBlock}>
        <h3 style={styles.title}>Overwinter DC Calculator</h3>
        <p style={styles.subtitle}>Lawson &amp; Dalrymple (1996)</p>
      </div>

      {/* Row 1: Fall DC + Winter Precip */}
      <div style={styles.grid}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Fall DC (Oct 31)</label>
          <input
            style={styles.input}
            type="number"
            value={fallDC}
            onChange={(e) => setFallDC(e.target.value)}
            placeholder="e.g., 400"
            min={0}
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Winter Precip (mm)</label>
          <input
            style={styles.input}
            type="number"
            value={winterPrecip}
            onChange={(e) => setWinterPrecip(e.target.value)}
            placeholder="e.g., 150"
            min={0}
          />
        </div>
      </div>

      {/* Row 2: Category selects */}
      <div style={styles.grid}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Carryover Category</label>
          <select
            style={styles.select}
            value={carryoverCategory}
            onChange={(e) => setCarryoverCategory(e.target.value as CarryoverCategory)}
          >
            <option value="high">High</option>
            <option value="moderate">Moderate</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Wetting Category</label>
          <select
            style={styles.select}
            value={wettingCategory}
            onChange={(e) => setWettingCategory(e.target.value as WettingCategory)}
          >
            <option value="high">High</option>
            <option value="moderate">Moderate</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Derived parameter display */}
      <div style={styles.paramDisplay}>
        Carryover Fraction: <strong>{params.carryoverFraction.toFixed(2)}</strong>
        {' | '}
        Wetting Efficiency: <strong>{params.wettingEfficiency.toFixed(2)}</strong>
      </div>

      {/* Calculate button */}
      <button
        style={isCalculateDisabled ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
        onClick={handleCalculate}
        disabled={isCalculateDisabled}
      >
        Calculate Spring DC
      </button>

      {/* Error display */}
      {error && (
        <div style={styles.errorBox}>{error}</div>
      )}

      {/* Result display */}
      {result !== null && (
        <div style={styles.resultBox}>
          Calculated Spring DC: <strong>{result.toFixed(1)}</strong>
        </div>
      )}

      {/* Use This Value button */}
      <button
        style={result === null ? { ...styles.buttonUse, ...styles.buttonDisabled } : styles.buttonUse}
        onClick={() => result !== null && onUseValue(result)}
        disabled={result === null}
      >
        Use This Value
      </button>
    </div>
  );
};

export default DCCalculatorPanel;
